// ============================================================
// AS VISTAS SALVAS DAS LISTAS (Story 31.3, Epic 31)
//
// Uma vista é a configuração de uma lista com nome, guardada como os PARÂMETROS DO
// URL da tela — o mesmo formato que a tela já lê e escreve (Story 31.2). Este
// ficheiro é o contrato entre a tela e `/api/list-views`: os corpos dos pedidos e
// a regra do que uma vista pode guardar.
//
// ⚠️ A REGRA DOS PARÂMETROS VIVE AQUI, E O SERVIDOR APLICA-A — não depende da tela.
// Uma vista NÃO guarda:
//   • `q` — a busca é texto livre, e pode ser um nome ou um telefone. Numa vista
//     partilhada, isso seria dado pessoal guardado e mostrado à equipe sem que
//     ninguém o tivesse decidido;
//   • `page`, `pageSize` e `view` — não são configuração (e uma vista dentro de
//     uma vista seria circular).
// O resto valida-se com o parse ESTRITO da API: um filtro que a API recusaria
// também não entra numa vista. Ler continua tolerante (a tela lê campo a campo),
// porque uma vista antiga pode ter um valor que deixou de existir.
// ============================================================
import { z } from 'zod'

import {
  PROFESSIONAL_LIST_FORMATS,
  parseProfessionalListQuery,
  professionalListQueryToParams,
} from './professional-list.ts'

export const LIST_VIEW_SCREENS = ['professionals'] as const
export type ListViewScreen = (typeof LIST_VIEW_SCREENS)[number]

export const LIST_VIEW_NAME_MAX = 60
export const LIST_VIEW_PARAMS_MAX = 2000
/** Quantas vistas próprias um usuário pode ter por tela. Um teto, não um plano. */
export const LIST_VIEWS_PER_USER_MAX = 50

/** As vistas da casa vivem no código da tela, com este prefixo: `casa:todos`. */
export function isHouseViewId(id: string): boolean {
  return /^casa:[a-z][a-z-]{0,39}$/.test(id)
}

/** O que uma vista nunca guarda. Ver o cabeçalho. */
const NAO_SE_GUARDA = ['q', 'page', 'pageSize', 'view'] as const

/** Os parâmetros que a API da lista de profissionais entende — validados pelo parse dela. */
const DA_API = [
  'archived',
  'sort',
  'situation',
  // Story 38.4 — o filtro do vínculo com a Genesis.
  'bond',
  'state',
  'workModel',
  'relocate',
  'contract',
  'noticeMaxDays',
  // Story 31.6. Os ids são validados pela FORMA aqui; a API confirma ao gravar que
  // existem no catálogo — um id não é texto livre a guardar numa vista partilhada.
  'area',
  'role',
  'skill',
  'skillMode',
  'seniority',
  // Story 31.7 — «só evidenciadas».
  'evidenced',
  // Story 31.10 — o agrupamento.
  'group',
] as const

/** As colunas são da tela, que as conhece; aqui só se garante a forma e o teto. */
const COLUNAS_MAX = 40
const COLUNA_ID = /^[a-zA-Z]{1,40}$/

/** Uma UF: o que o filtro da tela oferece. Texto livre aqui seria dado guardado à revelia. */
const UF = /^[A-Z]{2}$/

/**
 * `params` é a forma CANÓNICA do que passou na validação, e é ela que se grava —
 * nunca o texto que chegou. Reescrita a partir do parse, a vista só pode conter o
 * que tem forma conhecida: filtros de enum, UFs, a ordenação, ids de coluna e o formato.
 */
export type ViewParamsCheck = { ok: true; params: string } | { ok: false; error: string }

/**
 * Os parâmetros que uma vista pode guardar, verificados um a um.
 *
 * ⚠️ MAIS ESTRITO DO QUE O PARSE DA API, de propósito (revisão de segurança da
 * 31.3): a API tolera `archived` com qualquer valor (fail-closed) e aceita a UF
 * como texto livre (a coluna é livre), e isso é certo para LER. Para GUARDAR numa
 * vista que a equipe inteira pode ver, texto livre seria uma porta para nomes e
 * telefones — então `archived` só é `true`/`false`, `state` só são UFs, e uma
 * chave repetida é recusada (validar-se-ia só o último valor).
 */
export function validateViewParams(_screen: ListViewScreen, params: string): ViewParamsCheck {
  if (params.length > LIST_VIEW_PARAMS_MAX) {
    return { ok: false, error: `Os parâmetros passam de ${LIST_VIEW_PARAMS_MAX} caracteres.` }
  }
  const lidos = new URLSearchParams(params)
  const vistos = new Set<string>()
  const daApi: Record<string, string> = {}
  let cols: string | null = null
  let formato: string | null = null
  for (const [chave, valor] of lidos) {
    if (vistos.has(chave)) {
      return { ok: false, error: `O parâmetro «${chave}» aparece repetido.` }
    }
    vistos.add(chave)
    if ((NAO_SE_GUARDA as readonly string[]).includes(chave)) {
      return { ok: false, error: `O parâmetro «${chave}» não é salvo numa vista.` }
    }
    if (chave === 'cols') {
      const ids = valor.split(',')
      if (ids.length > COLUNAS_MAX || !ids.every((id) => COLUNA_ID.test(id))) {
        return { ok: false, error: 'As colunas da vista não são válidas.' }
      }
      cols = ids.join(',')
      continue
    }
    // O formato (Story 31.8) é da tela, como as colunas: um dos três, e a tabela —
    // a de sempre — não se escreve.
    if (chave === 'format') {
      if (!(PROFESSIONAL_LIST_FORMATS as readonly string[]).includes(valor)) {
        return { ok: false, error: 'O formato da vista não é válido.' }
      }
      formato = valor === 'table' ? null : valor
      continue
    }
    if (!(DA_API as readonly string[]).includes(chave)) {
      return { ok: false, error: `O parâmetro «${chave}» não existe nesta lista.` }
    }
    if (chave === 'archived' && valor !== 'true' && valor !== 'false') {
      return { ok: false, error: 'O parâmetro «archived» só aceita true ou false.' }
    }
    daApi[chave] = valor
  }
  const parsed = parseProfessionalListQuery(daApi)
  if (!parsed.success) {
    const campos = Object.keys(parsed.error.flatten().fieldErrors).join(', ')
    return { ok: false, error: `Valores inválidos na vista: ${campos}.` }
  }
  if (parsed.data.filters.state?.some((uf) => !UF.test(uf))) {
    return { ok: false, error: 'O filtro de UF só aceita siglas de estado.' }
  }
  const canonico = new URLSearchParams(
    professionalListQueryToParams({
      archived: parsed.data.archived,
      sort: parsed.data.sort,
      filters: parsed.data.filters,
      ...(parsed.data.group ? { group: parsed.data.group } : {}),
    }),
  )
  if (cols !== null) canonico.set('cols', cols)
  if (formato !== null) canonico.set('format', formato)
  return { ok: true, params: canonico.toString() }
}

/**
 * O nome: aparado, curto, e sem caracteres de controle, bidi ou de largura zero
 * (`\p{C}`) — com eles, uma vista da equipe podia imitar o nome de uma da casa.
 */
const Nome = z
  .string()
  .trim()
  .min(1)
  .max(LIST_VIEW_NAME_MAX)
  .refine((nome) => !/\p{C}/u.test(nome), { message: 'O nome tem caracteres inválidos.' })
const Params = z.string().max(LIST_VIEW_PARAMS_MAX)

export const CreateListViewSchema = z
  .object({
    screen: z.enum(LIST_VIEW_SCREENS),
    name: Nome,
    params: Params,
    shared: z.boolean().default(false),
  })
  .strict()
export type CreateListView = z.infer<typeof CreateListViewSchema>

export const UpdateListViewSchema = z
  .object({ name: Nome.optional(), params: Params.optional(), shared: z.boolean().optional() })
  .strict()
  .refine((v) => v.name !== undefined || v.params !== undefined || v.shared !== undefined, {
    message: 'Nada para alterar.',
  })
export type UpdateListView = z.infer<typeof UpdateListViewSchema>

export const PutLastOpenedSchema = z
  .object({ screen: z.enum(LIST_VIEW_SCREENS), viewId: z.string().min(1).max(64) })
  .strict()

/** Uma vista como a API a devolve. `mine`: o usuário que pede é o dono. */
export interface ListViewDto {
  id: string
  screen: ListViewScreen
  name: string
  params: string
  shared: boolean
  mine: boolean
  ownerName: string
  updatedAt: string
}

export interface ListViewsResponse {
  views: ListViewDto[]
  /** A última vista aberta, já verificada: `null` cai para a vista «Todos». */
  lastOpenedViewId: string | null
}
